import React, { useState } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import {
  TablePaymentMethod,
  CustomInput,
  Button,
  getIcon,
  getImage,
} from "@dishopsaas/design-system";
import { showPaymentModal, showMessageModal } from "../../../redux/actions";
import { deleteCB, sortedMethods } from "../../../utils/payment";
import {
  TRANSPARENT,
  TEXT_VALIDATION_DELETE_CB,
  PAYMENT,
  ICONS,
  ERROR,
} from "../../../constants";
import { getPaymentTypes } from "../PaymentSummary.services";
import { getPaymentTypeData } from "../../../utils/order";
import { CollapsibleCardBox } from "../../../components/Collapse/Collapse";
import { PaymentMethodGroupProps } from "../../../1_types/paymentSummaryTypes";
import { getColors } from "../../../utils";

const noHoverClickSx = {
  p: 0,
  "&, &:hover, &:active, &:focus": {
    backgroundColor: "transparent",
    boxShadow: "none",
    outline: "none",
  },
};

const EmptyState = () => (
  <Box
    sx={{
      display: "flex",
      p: 3,
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: 2,
      alignSelf: "stretch",
    }}
  >
    {getImage("card", { size: 80 })}
    <Typography variant="body1" sx={{ fontWeight: 700 }}>
      Aucune carte enregistrée
    </Typography>
  </Box>
);

const PaymentMethodGroup: React.FC<PaymentMethodGroupProps> = ({
  secondaryColor,
  selected,
  setSelected,
  cards = [],
  dispatch,
  paymentType,
  setSelectedPaymentType,
  isMobile,
}) => {
  const [open, setOpen] = useState(true);
  const isStripe = paymentType === PAYMENT.TYPE_STRIPE;

  const handleAddNewCardClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch?.(showPaymentModal(true));
  };

  const paymentGroups = getPaymentTypes().reduce(
    (
      groups: { online: React.ReactNode[]; onSite: React.ReactNode[] },
      type,
      index
    ) => {
      const { paymentTypeLabel, isOnSitePayment, icon } =
        getPaymentTypeData(type);
      const { border } = getColors();
      const element = (
        <Box
          key={type}
          onClick={() => setSelectedPaymentType?.(type)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            py: 3,
            pr: 3,
            cursor: "pointer",
            borderTop: index === 0 ? "none" : border,
            borderBottom:
              index === 0 && paymentType === PAYMENT.TYPE_STRIPE
                ? border
                : "none",
          }}
        >
          <Box sx={{ px: 1 }}>
            <CustomInput
              type="radio"
              checked={paymentType === type}
              onChange={() => setSelectedPaymentType?.(type)}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {getIcon(icon, { size: 20 })}
            <Typography sx={{ pl: 2 }} variant="body1">
              {paymentTypeLabel}
            </Typography>
          </Box>
        </Box>
      );
      if (isOnSitePayment) groups.onSite.push(element);
      else groups.online.push(element);
      return groups;
    },
    { online: [], onSite: [] }
  );
  const { paymentTypeLabel } = getPaymentTypeData(paymentType);
  const { border, bglightgrey } = getColors();
  return (
    <CollapsibleCardBox
      title={
        <>
          Méthode de paiement
          <Typography
            variant="body1"
            sx={{ textTransform: "none", fontWeight: 400, pt: 1 }}
          >
            {paymentTypeLabel}
          </Typography>
        </>
      }
    >
      <Box
        sx={{ border, borderRadius: 1, m: 3 }}
        display="flex"
        flexDirection="column"
        onClick={isStripe ? () => setOpen(true) : undefined}
      >
        {paymentGroups.online}
        {isStripe && (
          <Collapse
            in={open}
            timeout={500}
            sx={{
              opacity: open ? 1 : 0,
              transform: open ? "translateY(0)" : "translateY(-20px)",
            }}
          >
            <Box sx={{ px: 3, pb: 3, bgcolor: bglightgrey }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{ fontWeight: "bold" }}
                  variant={isMobile ? "caption" : "body1"}
                >
                  Cartes enregistrées
                </Typography>
                <Button
                  disableRipple
                  sx={noHoverClickSx}
                  iconOptions={{
                    color: secondaryColor,
                    size: isMobile ? 12 : 14,
                  }}
                  labelSx={{
                    color: secondaryColor,
                    fontSize: isMobile ? 12 : 14,
                  }}
                  variant="text"
                  rightIcon={ICONS.ChevronRight}
                  leftIcon={ICONS.Plus}
                  label="Ajouter une nouvelle carte"
                  colorType={TRANSPARENT}
                  borderRadius={8}
                  onClick={handleAddNewCardClick}
                />
              </Box>

              {cards.length === 0 ? (
                <EmptyState />
              ) : (
                <TablePaymentMethod
                  methods={sortedMethods(cards)}
                  selectedId={selected}
                  onSelect={setSelected}
                  onDelete={(id: string) =>
                    dispatch?.(
                      showMessageModal(TEXT_VALIDATION_DELETE_CB, {
                        onClick: () => deleteCB(id),
                        showCancel: true,
                        primaryColor: ERROR,
                        text: 'Supprimer votre carte bancaire ?',
                        buttonName: "supprimer ma carte",
                        subTitle: TEXT_VALIDATION_DELETE_CB,
                        withImage: true,
                        image: "error",
                        maxWidth: 480
                      })
                    )
                  }
                />
              )}
            </Box>
          </Collapse>
        )}
        {paymentGroups.onSite}
      </Box>
    </CollapsibleCardBox>
  );
};

export default PaymentMethodGroup;
